/**
 * Modal de Confirmação — Pedido Falta (Fase 3, Conferência de Saída Web)
 *
 * Exibe o resumo dos itens faltantes de uma conferência parcial e permite
 * o usuário confirmar a geração de um novo Pedido de Saída ("Pedido Falta").
 *
 * Regras:
 *  • Somente para saídas com status 60 (Conferida) e qtd_sup < qtd_req em ≥ 1 linha.
 *  • O novo pedido NÃO reserva estoque automaticamente.
 *  • Não é possível gerar Pedido Falta duplicado para a mesma origem.
 *
 * Uso:
 *   import { openShortageModal } from './outputs-shortage.js';
 *   openShortageModal(companyId, outputId, outputOrderId, outputItems);
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';

function escapeHtml(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/**
 * Calcula os itens faltantes de uma saída.
 * @param {Array} items - itens do output (com quantity_required / quantity_supplied)
 * @returns {Array} itens com shortage > 0
 */
function calcShortageItems(items) {
	return (items || []).filter(item => {
		const req = parseInt(item.quantity_required ?? 0, 10);
		const sup = parseInt(item.quantity_supplied ?? req, 10);
		return (req - sup) > 0;
	}).map(item => {
		const req = parseInt(item.quantity_required ?? 0, 10);
		const sup = parseInt(item.quantity_supplied ?? req, 10);
		return { ...item, shortage: req - sup };
	});
}

/**
 * Abre o modal de confirmação do Pedido Falta.
 *
 * @param {string} companyId      - UUID da empresa
 * @param {number} outputId       - ID da saída física (source_output_id)
 * @param {number} outputOrderId  - ID do pedido de saída original
 * @param {Array}  outputItems    - itens da saída (enriquecidos com quantity_required, product_name, product_sku)
 * @param {Function} [onCreated]  - callback chamado após criação bem-sucedida
 */
export function openShortageModal(companyId, outputId, outputOrderId, outputItems, onCreated) {
	const shortageItems = calcShortageItems(outputItems);

	if (!shortageItems.length) {
		notify('Esta saída não possui itens faltantes. Nenhum Pedido Falta necessário.', 'info');
		return;
	}

	// ─── Tabela de itens faltantes ────────────────────────────────────────────

	const rows = shortageItems.map(item => {
		const sku = item.product_sku
			? (Array.isArray(item.product_sku) ? item.product_sku.join(' ') : item.product_sku)
			: '';
		const name = item.product_name || `Produto #${item.product_id}`;
		const lot  = item.lot_number || '—';
		const req  = parseInt(item.quantity_required ?? 0, 10);
		const sup  = parseInt(item.quantity_supplied  ?? req, 10);

		return `
			<tr>
				<td style="padding:6px 10px;border-bottom:1px solid #eee;font-size:13px">
					<span style="color:#666;font-size:11px">${escapeHtml(sku)}</span><br>
					<strong>${escapeHtml(name)}</strong>
				</td>
				<td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center;font-size:13px">${escapeHtml(lot)}</td>
				<td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center;font-size:13px">${escapeHtml(req)}</td>
				<td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center;font-size:13px;color:#1a7a3c">${escapeHtml(sup)}</td>
				<td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center;font-weight:bold;color:#c0392b;font-size:13px">${escapeHtml(item.shortage)}</td>
			</tr>`;
	}).join('');

	const tableHtml = `
		<table style="width:100%;border-collapse:collapse;margin-top:8px">
			<thead>
				<tr style="background:#f5f5f5">
					<th style="padding:7px 10px;text-align:left;font-size:12px;font-weight:600;color:#555">Produto</th>
					<th style="padding:7px 10px;text-align:center;font-size:12px;font-weight:600;color:#555">Lote</th>
					<th style="padding:7px 10px;text-align:center;font-size:12px;font-weight:600;color:#555">Req.</th>
					<th style="padding:7px 10px;text-align:center;font-size:12px;font-weight:600;color:#555">Conf.</th>
					<th style="padding:7px 10px;text-align:center;font-size:12px;font-weight:600;color:#c0392b">Falta</th>
				</tr>
			</thead>
			<tbody>${rows}</tbody>
		</table>`;

	const totalShortage = shortageItems.reduce((s, i) => s + i.shortage, 0);

	const html = `
		<div class="msgbox" style="max-width:700px;min-width:400px">
			<div class="mb-title">Pedido Falta — Saída #${escapeHtml(outputId)}</div>
			<div class="mb-text">
				<div style="background:#fff8e1;border-left:4px solid #f0a500;padding:12px 16px;border-radius:4px;margin-bottom:16px">
					<strong style="color:#7a5200">Confirmação — Gerar Pedido Falta</strong><br>
					<span style="color:#555;font-size:13px">
						Será criado um novo Pedido de Saída contendo apenas as quantidades faltantes
						da saída <strong>#${escapeHtml(outputId)}</strong> (pedido <strong>#${escapeHtml(outputOrderId)}</strong>).
						O novo pedido <strong>não reservará estoque automaticamente</strong>;
						seguirá o fluxo normal de aprovação → geração → separação → conferência.
					</span>
				</div>
				${tableHtml}
				<div style="margin-top:12px;text-align:right;font-size:13px;color:#555">
					Total de unidades faltantes: <strong style="color:#c0392b">${escapeHtml(totalShortage)}</strong>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Gerar Pedido Falta</button>
		</div>`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		// api.call mostra spinner, trata erros e retorna o JSON parseado (ou undefined em erro)
		const result = await api.call(
			api.create_shortage_order,
			companyId,
			outputOrderId,
			{ source_output_id: outputId, grouping: 'by_original_order' }
		);

		if (!result) {
			// api.call já exibiu a notificação de erro; apenas manter o modal aberto
			return;
		}

		dlg.destroy();
		notify(
			`Pedido Falta criado com sucesso!\nPedido #${result.id} — Referência: ${result.reference}`,
			'Pedido Falta'
		);
		if (typeof onCreated === 'function') onCreated(result);

	}, /Escape|N|Y/);
}

/**
 * Verifica se uma lista de itens de saída possui falta (qtd_req > qtd_sup).
 * @param {Array} items
 * @returns {boolean}
 */
export function hasShortage(items) {
	return calcShortageItems(items).length > 0;
}
